/**
 * Resolve what a quantity change on a product-store tile means for the order.
 *
 * The store's minus button rendered for every line with quantity > 0 and was
 * wired to an empty stub — `handleRemove` in single-product.tsx, and both
 * handlers in product-modal.tsx, were left commented out when ProductContext
 * was deleted and only the *add* path was migrated to the API. Customers who
 * added the wrong product saw a remove button, pressed it, and nothing
 * happened; the only way out was the cart drawer, which they had no reason to
 * think of.
 *
 * Decrementing is two different API calls depending on where it lands (update
 * the line, or delete it), and the store knows products by product id while the
 * order knows them by order-product-detail id. Both callers need exactly that
 * translation, so it lives here rather than being written twice.
 */
export type CartQuantityAction =
  /** Line exists and stays — set it to `quantity`. */
  | { action: "update"; orderProductDetailId: string; quantity: number }
  /** Line drops to zero (or below) — delete it outright. */
  | { action: "remove"; orderProductDetailId: string }
  /** Nothing to do: no such line, or the quantity is already what was asked. */
  | { action: "noop" };

/** The subset of an order product detail this decision needs. */
export type CartDetail = {
  id: string;
  quantity: number;
  product?: { id?: string } | null;
  productId?: string;
};

/** An order line's product id, which the API returns in either of two shapes. */
const detailProductId = (detail: CartDetail): string | undefined =>
  detail.product?.id ?? detail.productId;

export function resolveCartQuantityChange(
  details: readonly CartDetail[],
  productId: string,
  nextQuantity: number,
): CartQuantityAction {
  const detail = details.find(
    (d) => detailProductId(d) === productId && d.quantity > 0,
  );

  // Nothing in the cart for this product. Adding is the add-product endpoint's
  // job, not this one's — returning noop keeps a stray minus click harmless.
  if (!detail) {
    return { action: "noop" };
  }

  // Guard against a non-integer or negative argument reaching the API, where
  // `quantity < 1` is a 400 rather than a delete.
  const target = Math.floor(nextQuantity);

  if (target <= 0) {
    return { action: "remove", orderProductDetailId: detail.id };
  }
  if (target === detail.quantity) {
    return { action: "noop" };
  }
  return { action: "update", orderProductDetailId: detail.id, quantity: target };
}
